package org.dronda.app.web.ui.wishlist

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import org.dronda.api.wishlist.WishlistItem
import org.dronda.app.web.Loader
import org.dronda.app.web.ui.components.ControlledInput
import org.dronda.app.web.ui.components.Dialog
import org.dronda.app.web.ui.components.ControlledTextArea
import org.dronda.app.web.ui.components.StyledButton
import org.dronda.app.web.ui.util.rememberWindowSize
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.rgb
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun WishlistScreen(
    viewModel: WishlistViewModel,
    onBackClick: () -> Unit
) {
    val uiState by viewModel.uiState.collectAsState()
    val dialogState by viewModel.dialogState.collectAsState()
    val windowSize by rememberWindowSize()
    val mainWidth by remember(windowSize) {
        mutableStateOf(
            if (windowSize.width > 800) {
                70.percent
            } else {
                90.percent
            }
        )
    }

    Row(
        modifier = Modifier.fillMaxWidth()
            .padding(top = 50.px),
        horizontalArrangement = Arrangement.Center,
    ) {
        Column(
            modifier = Modifier.width(mainWidth),
            verticalArrangement = Arrangement.spacedBy(1.cssRem)
        ) {
            // Back button
            Span(
                Modifier
                    .fontSize(1.5.cssRem)
                    .cursor(Cursor.Pointer)
                    .onClick { onBackClick() }
                    .toAttrs()
            ) {
                Text("◀ Back")
            }

            when (val state = uiState) {
                is WishlistViewModel.UiState.Loading -> {
                    Loader()
                }
                is WishlistViewModel.UiState.Success -> {
                    val wishlist = state.wishlist

                    H1(Modifier.fontWeight(FontWeight.Light).toAttrs()) {
                        Text(if (wishlist.isOwner) "Your Wishlist" else "${wishlist.user.name}'s Wishlist")
                    }

                    // Section 1: Wishlist Items (what owner wants)
                    H2 { Text(if (wishlist.isOwner) "YOUR WISHLIST" else "WISHLIST (What they want)") }

                    // Show empty state card or items
                    if (wishlist.wishlistItems.isEmpty()) {
                        WishlistEmptyStateCard(
                            isOwner = wishlist.isOwner,
                            onAddClick = { viewModel.take(WishlistViewModel.ViewEvent.ShowAddItemDialog) }
                        )
                    } else {
                        wishlist.wishlistItems.forEach { item ->
                            WishlistItemCard(item, wishlist.isOwner, viewModel)
                        }

                        // Show "+ Add Item" button only if there are existing items and user is owner
                        if (wishlist.isOwner) {
                            StyledButton(
                                "+ Add Item",
                                onClick = { viewModel.take(WishlistViewModel.ViewEvent.ShowAddItemDialog) }
                            )
                        }
                    }

                    // Section 2: Purchased Items (what others bought)
                    if (!wishlist.isOwner && wishlist.purchasedItems.isNotEmpty()) {
                        H2 { Text("PURCHASED ITEMS") }
                        P { Text("Note: ${wishlist.user.name} cannot see this section") }

                        wishlist.purchasedItems.forEach { item ->
                            PurchasedItemCard(item, viewModel)
                        }
                    }

                    if (!wishlist.isOwner) {
                        StyledButton(
                            "+ Add Item I Bought",
                            onClick = { viewModel.take(WishlistViewModel.ViewEvent.ShowAddItemDialog) }
                        )
                    }

                    // Owner info banner
                    if (wishlist.isOwner) {
                        InfoBanner("Others are buying you gifts! You'll see them after the event.")
                    }
                }
                is WishlistViewModel.UiState.Error -> {
                    Text(state.message)
                }
            }
        }
    }

    if (dialogState !is WishlistViewModel.DialogState.Hidden) {
        val currentWishlist = (uiState as? WishlistViewModel.UiState.Success)?.wishlist
        ItemDialog(
            dialogState = dialogState,
            isOwner = currentWishlist?.isOwner ?: true,
            onDismiss = { viewModel.take(WishlistViewModel.ViewEvent.HideAddItemDialog) },
            onSubmit = { name, desc, url ->
                val visibleState = dialogState as? WishlistViewModel.DialogState.Visible
                if (visibleState?.isEditing == true && visibleState.editingItemId != null) {
                    viewModel.take(
                        WishlistViewModel.ViewEvent.UpdateItem(
                            itemId = visibleState.editingItemId,
                            name = name,
                            description = desc,
                            url = url
                        )
                    )
                } else {
                    viewModel.take(WishlistViewModel.ViewEvent.AddItem(name, desc, url))
                }
            }
        )
    }
}

@Composable
private fun WishlistItemCard(item: WishlistItem, isOwner: Boolean, viewModel: WishlistViewModel) {
    val isHovered = remember { mutableStateOf(false) }

    Div(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Colors.White)
            .borderRadius(8.px)
            .padding(topBottom = 1.cssRem, leftRight = 1.5.cssRem)
            .toAttrs {
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "1rem")
                    property("margin-bottom", "0.75rem")
                    property("border", "1px solid ${if (isHovered.value) "rgb(200, 200, 200)" else "rgb(230, 230, 230)"}")
                    property("transition", "all 0.2s ease")
                }
                onMouseEnter { isHovered.value = true }
                onMouseLeave { isHovered.value = false }
            }
    ) {
        val windowSize by rememberWindowSize()
        // Icon indicator
        if (windowSize.width > 800) {
            Div(
                Modifier
                    .width(40.px)
                    .height(40.px)
                    .backgroundColor(rgb(248, 245, 255))
                    .borderRadius(50.percent)
                    .fontSize(1.25.cssRem)
                    .toAttrs {
                        style {
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("flex-shrink", "0")
                        }
                    }
            ) {
                Text("🎁")
            }
        }

        // Main content area
        Div(
            Modifier.toAttrs {
                style {
                    property("flex-grow", "1")
                    property("min-width", "0")
                }
            }
        ) {
            // Name and URL badge row
            Div(
                Modifier.toAttrs {
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "0.5rem")
                        property("margin-bottom", "0.25rem")
                    }
                }
            ) {
                // Name (with hyperlink if URL exists)
                val url = item.url
                if (url != null) {
                    A(
                        href = if (url.startsWith("http")) {
                            url
                        } else {
                            "https://$url"
                        },
                        attrs = Modifier
                            .fontSize(1.05.cssRem)
                            .fontWeight(600)
                            .color(rgb(37, 99, 235))
                            .toAttrs {
                                attr("target", "_blank")
                                style {
                                    property("text-decoration", "underline")
                                    property("margin", "0")
                                }
                            }
                    ) {
                        Text(item.name)
                    }
                } else {
                    Span(
                        Modifier
                            .fontSize(1.05.cssRem)
                            .fontWeight(600)
                            .color(rgb(40, 40, 40))
                            .toAttrs()
                    ) {
                        Text(item.name)
                    }
                }

                // URL domain badge (if URL exists)
                item.url?.let { url ->
                    UrlDomainBadge(url)
                }
            }

            item.description?.let { desc ->
                P(
                    Modifier
                        .fontSize(0.9.cssRem)
                        .color(rgb(120, 120, 120))
                        .toAttrs {
                            style {
                                property("margin", "0")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                                property("white-space", "nowrap")
                            }
                        }
                ) {
                    Text(desc)
                }
            }
        }

        // Actions area
        Div(
            Modifier.toAttrs {
                style {
                    property("display", "flex")
                    property("gap", "0.75rem")
                    property("align-items", "center")
                }
            }
        ) {
            if (!isOwner) {
                val isPurchased = item.checkedUser != null || item.isPurchased
                val buttonText = if (isPurchased && item.checkedUser?.id == viewModel.currentUserId) {
                    "Unpurchase"
                } else if (isPurchased) {
                    "Purchased by someone else"
                } else {
                    "Mark Purchased"
                }
                val buttonColor = if (isPurchased) rgb(150, 150, 150) else rgb(100, 200, 100)

                Button(
                    attrs = Modifier
                        .backgroundColor(buttonColor)
                        .color(Colors.White)
                        .borderRadius(6.px)
                        .fontSize(0.9.cssRem)
                        .fontWeight(500)
                        .cursor(Cursor.Pointer)
                        .toAttrs {
                            style {
                                property("border", "0")
                                property("padding", "0.5rem 1rem")
                                property("transition", "background-color 0.2s ease")
                            }
                            onClick {
                                viewModel.take(WishlistViewModel.ViewEvent.TogglePurchaseStatus(item.id))
                            }
                        }
                ) {
                    Text(buttonText)
                }
            }

            // Edit and Delete buttons (visible for all users - they can only edit/delete their own items)
            if (isOwner) {
                Button(
                    attrs = Modifier
                        .backgroundColor(rgb(100, 150, 200))
                        .color(Colors.White)
                        .borderRadius(6.px)
                        .fontSize(0.9.cssRem)
                        .fontWeight(500)
                        .cursor(Cursor.Pointer)
                        .toAttrs {
                            style {
                                property("border", "0")
                                property("padding", "0.5rem 1rem")
                                property("transition", "background-color 0.2s ease")
                            }
                            onClick {
                                viewModel.take(
                                    WishlistViewModel.ViewEvent.ShowEditItemDialog(
                                        itemId = item.id,
                                        name = item.name,
                                        description = item.description,
                                        url = item.url
                                    )
                                )
                            }
                        }
                ) {
                    Text("Edit")
                }

                Button(
                    attrs = Modifier
                        .backgroundColor(rgb(200, 100, 100))
                        .color(Colors.White)
                        .borderRadius(6.px)
                        .fontSize(0.9.cssRem)
                        .fontWeight(500)
                        .cursor(Cursor.Pointer)
                        .toAttrs {
                            style {
                                property("border", "0")
                                property("padding", "0.5rem 1rem")
                                property("transition", "background-color 0.2s ease")
                            }
                            onClick {
                                viewModel.take(WishlistViewModel.ViewEvent.DeleteItem(item.id))
                            }
                        }
                ) {
                    Text("Delete")
                }
            }
        }
    }
}

@Composable
private fun WishlistEmptyStateCard(isOwner: Boolean, onAddClick: () -> Unit) {
    Div(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Colors.White)
            .borderRadius(8.px)
            .padding(topBottom = 1.5.cssRem, leftRight = 1.5.cssRem)
            .toAttrs {
                style {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("align-items", "center")
                    property("gap", "1rem")
                    property("margin-bottom", "0.75rem")
                    property("border", "1px solid rgb(230, 230, 230)")
                }
            }
    ) {
        // Gift icon in circle (matching WishlistItemCard style but more vibrant)
        Div(
            Modifier
                .width(48.px)
                .height(48.px)
                .backgroundColor(rgb(139, 92, 246))
                .borderRadius(50.percent)
                .toAttrs {
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("font-size", "1.5rem")
                        property("flex-shrink", "0")
                    }
                }
        ) {
            Text("🎁")
        }

        // Empty state text
        P(
            Modifier
                .fontSize(1.cssRem)
                .color(rgb(100, 100, 100))
                .toAttrs {
                    style {
                        property("margin", "0")
                        property("text-align", "center")
                    }
                }
        ) {
            Text(if (isOwner) "No items in this wishlist yet" else "This wishlist is empty")
        }

        // Add item button (only for owners)
        if (isOwner) {
            StyledButton(
                "+ Add Your First Item",
                onClick = { onAddClick() }
            )
        }
    }
}

@Composable
private fun PurchasedItemCard(item: WishlistItem, viewModel: WishlistViewModel) {
    Div(
        Modifier
            .fillMaxWidth()
            .backgroundColor(rgb(248, 250, 248))
            .borderRadius(8.px)
            .padding(topBottom = 1.cssRem, leftRight = 1.5.cssRem)
            .toAttrs {
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "1rem")
                    property("margin-bottom", "0.75rem")
                    property("border", "1px solid rgb(220, 235, 220)")
                }
            }
    ) {
        // Checkmark icon
        Div(
            Modifier
                .width(40.px)
                .height(40.px)
                .backgroundColor(rgb(230, 245, 230))
                .borderRadius(50.percent)
                .color(rgb(80, 180, 80))
                .fontWeight(700)
                .toAttrs {
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("font-size", "1.25rem")
                        property("flex-shrink", "0")
                    }
                }
        ) {
            Text("✓")
        }

        // Content area
        Div(
            Modifier.toAttrs {
                style {
                    property("flex-grow", "1")
                }
            }
        ) {
            // Name and URL badge row
            Div(
                Modifier.toAttrs {
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "0.5rem")
                        property("margin-bottom", "0.25rem")
                    }
                }
            ) {
                // Name (with hyperlink if URL exists)
                val url = item.url
                if (url != null) {
                    A(
                        href = if (url.startsWith("http")) {
                            url
                        } else {
                            "https://$url"
                        },
                        attrs = Modifier
                            .fontSize(1.05.cssRem)
                            .fontWeight(600)
                            .color(rgb(37, 99, 235))
                            .toAttrs {
                                attr("target", "_blank")
                                style {
                                    property("text-decoration", "underline")
                                    property("margin", "0")
                                }
                            }
                    ) {
                        Text(item.name)
                    }
                } else {
                    Span(
                        Modifier
                            .fontSize(1.05.cssRem)
                            .fontWeight(600)
                            .color(rgb(40, 40, 40))
                            .toAttrs()
                    ) {
                        Text(item.name)
                    }
                }

                // URL domain badge (if URL exists)
                item.url?.let { url ->
                    UrlDomainBadge(url)
                }
            }

            // Description
            item.description?.let { desc ->
                P(
                    Modifier
                        .fontSize(0.9.cssRem)
                        .color(rgb(120, 120, 120))
                        .toAttrs {
                            style {
                                property("margin", "0")
                                property("margin-bottom", "0.25rem")
                            }
                        }
                ) {
                    Text(desc)
                }
            }

            val text = if (item.addedByUserId == viewModel.currentUserId) {
                "(You bought this)"
            } else {
                "(Someone bought this)"
            }
            P(
                Modifier
                    .fontSize(0.85.cssRem)
                    .color(rgb(120, 120, 120))
                    .toAttrs {
                        style {
                            property("margin", "0")
                        }
                    }
            ) {
                Text(text)
            }
        }

        // Edit and Delete buttons (only if user added this item)
        if (item.addedByUserId == viewModel.currentUserId) {
            Div(
                Modifier.toAttrs {
                    style {
                        property("display", "flex")
                        property("gap", "0.5rem")
                        property("flex-shrink", "0")
                    }
                }
            ) {
                Button(
                    attrs = Modifier
                        .backgroundColor(rgb(100, 150, 200))
                        .color(Colors.White)
                        .borderRadius(6.px)
                        .fontSize(0.85.cssRem)
                        .fontWeight(500)
                        .cursor(Cursor.Pointer)
                        .toAttrs {
                            style {
                                property("border", "0")
                                property("padding", "0.4rem 0.8rem")
                            }
                            onClick {
                                viewModel.take(
                                    WishlistViewModel.ViewEvent.ShowEditItemDialog(
                                        itemId = item.id,
                                        name = item.name,
                                        description = item.description,
                                        url = item.url
                                    )
                                )
                            }
                        }
                ) {
                    Text("Edit")
                }

                Button(
                    attrs = Modifier
                        .backgroundColor(rgb(200, 100, 100))
                        .color(Colors.White)
                        .borderRadius(6.px)
                        .fontSize(0.85.cssRem)
                        .fontWeight(500)
                        .cursor(Cursor.Pointer)
                        .toAttrs {
                            style {
                                property("border", "0")
                                property("padding", "0.4rem 0.8rem")
                            }
                            onClick {
                                viewModel.take(WishlistViewModel.ViewEvent.DeleteItem(item.id))
                            }
                        }
                ) {
                    Text("Delete")
                }
            }
        }
    }
}

@Composable
private fun UrlDomainBadge(url: String) {
    Span(
        Modifier
            .fontSize(0.75.cssRem)
            .padding(topBottom = 0.15.cssRem, leftRight = 0.5.cssRem)
            .backgroundColor(rgb(230, 235, 240))
            .color(rgb(80, 100, 120))
            .borderRadius(12.px)
            .toAttrs {
                style {
                    property("white-space", "nowrap")
                }
            }
    ) {
        // Extract domain from URL
        val domain = url.substringAfter("://").substringBefore("/")
        Text(domain)
    }
}

@Composable
private fun InfoBanner(message: String) {
    Div(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Colors.LightBlue)
            .borderRadius(6.px)
            .padding(1.cssRem)
            .toAttrs()
    ) {
        Text("ℹ️  $message")
    }
}

@Composable
private fun ItemDialog(
    dialogState: WishlistViewModel.DialogState,
    isOwner: Boolean,
    onDismiss: () -> Unit,
    onSubmit: (name: String, description: String?, url: String?) -> Unit
) {
    val visibleState = dialogState as? WishlistViewModel.DialogState.Visible
    val itemName = remember(dialogState) { mutableStateOf(visibleState?.name ?: "") }
    val itemDescription = remember(dialogState) { mutableStateOf(visibleState?.description ?: "") }
    val itemUrl = remember(dialogState) { mutableStateOf(visibleState?.url ?: "") }

    val isEditing = visibleState?.isEditing == true
    val title = when {
        isEditing -> "Edit Item"
        isOwner -> "Add Wishlist Item"
        else -> "Add Purchased Item"
    }

    Dialog(title = title, onDismiss = onDismiss) {
        when (dialogState) {
            is WishlistViewModel.DialogState.Visible -> {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(1.cssRem)
                ) {
                    ControlledInput(
                        textState = itemName,
                        label = "Item Name",
                        labelId = "item-name-input",
                        inputType = InputType.Text,
                        modifier = Modifier.width(100.percent)
                    )

                    ControlledTextArea(
                        textState = itemDescription,
                        label = "Description (optional)",
                        labelId = "item-description-input",
                        rowCount = 3,
                        modifier = Modifier.width(100.percent)
                    )

                    ControlledInput(
                        textState = itemUrl,
                        label = "URL (optional)",
                        labelId = "item-url-input",
                        inputType = InputType.Url,
                        modifier = Modifier.width(100.percent)
                    )

                    Row(
                        modifier = Modifier.fillMaxWidth()
                            .gap(0.5.cssRem),
                        horizontalArrangement = Arrangement.End,
                        verticalAlignment = com.varabyte.kobweb.compose.ui.Alignment.CenterVertically
                    ) {
                        StyledButton(
                            "Cancel",
                            onClick = { onDismiss() }
                        )
                        StyledButton(
                            if (dialogState.isEditing) "Save" else "Add",
                            onClick = {
                                if (itemName.value.isNotBlank()) {
                                    onSubmit(
                                        itemName.value,
                                        itemDescription.value.ifBlank { null },
                                        itemUrl.value.ifBlank { null }
                                    )
                                }
                            },
                            primary = true
                        )
                    }
                }
            }
            WishlistViewModel.DialogState.Submitting -> {
                Loader()
            }
            is WishlistViewModel.DialogState.Error -> {
                P(Modifier.color(rgb(200, 0, 0)).toAttrs()) {
                    Text(dialogState.message)
                }
            }
            WishlistViewModel.DialogState.Hidden -> {}
        }
    }
}
